import { useState, useEffect, useCallback, useRef } from 'react';
import { annotateOarsUtteranceStream } from '../services/api';

function useChatOarsMode({
  isOarsMode,
  oarsExercise,
  oarsResetKey,
  oarsPopupBlocking,
  inputMessage,
  setInputMessage,
  inputRef,
  messagesContainerRef,
  onAttempt,
  onOarsAnalyzingChange,
  onOarsStreamingEvaluationChange,
  triggerComposerLimitFlash,
  composerCharLimit,
  specificSkillAttemptId = null,
  retryOfAttemptId = null,
  selectedSkillType = null,
  autoFocusInput = true,
}) {
  const [oarsResult, setOarsResult] = useState(null);
  const [oarsSubmitting, setOarsSubmitting] = useState(false);
  const [oarsPendingUtterance, setOarsPendingUtterance] = useState(null);
  const activeStreamRef = useRef(null);

  useEffect(() => {
    activeStreamRef.current?.abort();
    activeStreamRef.current = null;
    if (!isOarsMode) {
      onOarsStreamingEvaluationChange?.(null);
      return;
    }
    setInputMessage('');
    setOarsResult(null);
    setOarsPendingUtterance(null);
    onOarsStreamingEvaluationChange?.(null);
    if (autoFocusInput) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [
    autoFocusInput,
    isOarsMode,
    oarsResetKey,
    setInputMessage,
    inputRef,
    onOarsStreamingEvaluationChange,
  ]);

  useEffect(() => () => activeStreamRef.current?.abort(), []);

  useEffect(() => {
    if (!isOarsMode) return;
    // Scroll inside the list, not via scrollIntoView, which would also scroll
    // the document on mobile.
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  }, [isOarsMode, oarsResult, messagesContainerRef]);

  useEffect(() => {
    if (isOarsMode && oarsExercise && !oarsPopupBlocking) {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [isOarsMode, oarsExercise, oarsPopupBlocking, messagesContainerRef]);

  const handleOarsSubmit = useCallback(async (e) => {
    e?.preventDefault();
    if (!oarsExercise || !inputMessage.trim() || oarsSubmitting || oarsResult) return;
    if (inputMessage.length > composerCharLimit) {
      triggerComposerLimitFlash();
      return;
    }

    const utterance = inputMessage.trim();
    setInputMessage('');
    setOarsPendingUtterance(utterance);
    setOarsSubmitting(true);
    onOarsAnalyzingChange?.(true);
    onOarsStreamingEvaluationChange?.({ feedback: '', predicted_labels: [] });

    try {
      const isDraftExercise =
        !oarsExercise.generated_exercise_id
        && (
          oarsExercise.id?.startsWith('SR_GEN_DRAFT_')
          || oarsExercise.id?.startsWith('CR_GEN_DRAFT_')
          || oarsExercise.id?.startsWith('AF_GEN_DRAFT_')
          || oarsExercise.id?.startsWith('OQ_GEN_DRAFT_')
          || oarsExercise.id?.startsWith('S_GEN_DRAFT_')
        );
      const draftExercisePayload = isDraftExercise
        ? (() => {
            const { draft_signature, ...draftPayload } = oarsExercise;
            return draftPayload;
          })()
        : null;
      const stream = annotateOarsUtteranceStream(
        oarsExercise.id,
        utterance,
        oarsExercise.generated_exercise_id || null,
        draftExercisePayload,
        isDraftExercise ? oarsExercise.draft_signature || null : null,
        {
          specificSkillAttemptId,
          selectedSkillType,
          retryOfAttemptId,
        },
        {
          onClassification: (classification) => {
            onOarsStreamingEvaluationChange?.((current) => ({
              ...(current || {}),
              ...classification,
            }));
          },
          onGuidance: (feedback) => {
            onOarsStreamingEvaluationChange?.((current) => ({
              ...(current || {}),
              feedback,
            }));
          },
        },
      );
      activeStreamRef.current = stream;
      const data = await stream.promise;
      const result = {
        ...data,
        utterance,
        oarsCategory: oarsExercise.oars_category,
        cr_strategy: data.cr_strategy || oarsExercise.cr_strategy || null,
      };
      setOarsResult(result);
      onAttempt?.(result);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('OARS annotation failed:', err);
        onOarsStreamingEvaluationChange?.(null);
      }
    } finally {
      activeStreamRef.current = null;
      setOarsSubmitting(false);
      onOarsAnalyzingChange?.(false);
    }
  }, [
    composerCharLimit,
    inputMessage,
    oarsExercise,
    oarsResult,
    oarsSubmitting,
    onAttempt,
    onOarsAnalyzingChange,
    onOarsStreamingEvaluationChange,
    setInputMessage,
    specificSkillAttemptId,
    retryOfAttemptId,
    selectedSkillType,
    triggerComposerLimitFlash,
  ]);

  return {
    oarsResult,
    oarsSubmitting,
    oarsPendingUtterance,
    handleOarsSubmit,
  };
}

export default useChatOarsMode;
